import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API = 'https://api.medlynk.me/api';

function App() {
  const [token, setToken] = useState(localStorage.getItem('fac_token') || null);
  const [facility, setFacility] = useState(null);
  const [specialisations, setSpecialisations] = useState([]);
  const [labServices, setLabServices] = useState([]);
  const [allSpecs, setAllSpecs] = useState([]);
  const [page, setPage] = useState('dashboard');
  const [phone, setPhone] = useState('+237');
  const [otp, setOtp] = useState('');
  const [authStep, setAuthStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(localStorage.getItem('fac_id') || null);
  const [newLabName, setNewLabName] = useState('');
  const [newLabNameEn, setNewLabNameEn] = useState('');
  const [newLabPrice, setNewLabPrice] = useState('');
  const [newLabHours, setNewLabHours] = useState('');

  useEffect(() => {
    if (token && selectedFacilityId) loadFacilityData();
  }, [token, selectedFacilityId]);

  const requestOTP = async () => {
    if (phone.length < 10) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/users/request-otp`, { phone_number: phone, language: 'fr' });
      if (res.data.success) {
        setAuthStep('otp');
        if (res.data.dev_otp) setOtp(res.data.dev_otp);
      }
    } catch (err) { alert('Erreur: ' + err.message); }
    finally { setLoading(false); }
  };

  const verifyOTP = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/users/verify-otp`, { phone_number: phone, otp_code: otp });
      if (res.data.success) {
        localStorage.setItem('fac_token', res.data.token);
        setToken(res.data.token);
        setAuthStep('select');
        loadFacilities();
      }
    } catch (err) { alert('Code invalide'); }
    finally { setLoading(false); }
  };

  const loadFacilities = async () => {
    try {
      const res = await axios.get(`${API}/facilities?city=Douala`);
      setFacilities(res.data.facilities || []);
    } catch (err) { console.log(err.message); }
  };

  const selectFacility = (id) => {
    localStorage.setItem('fac_id', id);
    setSelectedFacilityId(id);
    setAuthStep('done');
  };

  const logout = () => {
    localStorage.removeItem('fac_token');
    localStorage.removeItem('fac_id');
    setToken(null);
    setFacility(null);
    setAuthStep('phone');
    setPhone('+237');
    setOtp('');
    setSelectedFacilityId(null);
  };

  const loadFacilityData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/facilities/${selectedFacilityId}`);
      const data = res.data.facility || res.data;
      setFacility(data);
      setSpecialisations(res.data.specialisations || []);
      setLabServices(res.data.lab_services || []);
      const specsRes = await axios.get(`${API}/facilities/specialisations`);
      setAllSpecs(specsRes.data.specialisations || []);
    } catch (err) { console.log('Load error:', err.message); }
    finally { setLoading(false); }
  };

  const addSpecialisation = async (specId) => {
    try {
      await axios.post(`${API}/facilities/${selectedFacilityId}/specialisations`, {
        specialisation_id: specId
      });
      loadFacilityData();
    } catch (err) { alert('Erreur: ' + (err.response?.data?.message || err.message)); }
  };

  const addLabService = async () => {
    if (!newLabName) return;
    try {
      await axios.post(`${API}/facilities/${selectedFacilityId}/lab-services`, {
        name_fr: newLabName,
        name_en: newLabNameEn || newLabName,
        price_fcfa: parseInt(newLabPrice) || 0,
        turnaround_hrs: parseInt(newLabHours) || 24,
      });
      setNewLabName('');
      setNewLabNameEn('');
      setNewLabPrice('');
      setNewLabHours('');
      loadFacilityData();
    } catch (err) { alert('Erreur: ' + err.message); }
  };

  const getFacilityType = () => {
    if (!facility) return 'Établissement';
    const name = (facility.type_fr || facility.name_fr || '').toLowerCase();
    if (name.includes('hopital') || name.includes('hôpital') || name.includes('hospital')) return 'Hôpital';
    if (name.includes('clinique') || name.includes('clinic')) return 'Clinique';
    if (name.includes('laboratoire') || name.includes('lab')) return 'Laboratoire';
    if (name.includes('centre') || name.includes('center')) return 'Centre de Santé';
    if (name.includes('maternite') || name.includes('maternity')) return 'Maternité';
    return 'Établissement';
  };

  // LOGIN SCREEN
  if (!token || authStep === 'phone' || authStep === 'otp' || authStep === 'select') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <span className="logo-icon">🏥</span>
            <h1>Med<span>Lynk</span></h1>
            <p className="login-sub">Espace Établissement</p>
          </div>

          {authStep === 'phone' && (
            <>
              <label>Numéro de téléphone</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+237 6XX XXX XXX" />
              <button className="btn-primary" onClick={requestOTP} disabled={loading}>
                {loading ? 'Envoi...' : 'Envoyer le code'}
              </button>
            </>
          )}

          {authStep === 'otp' && (
            <>
              <p className="otp-hint">Code envoyé à {phone}</p>
              <input type="text" value={otp} onChange={e => setOtp(e.target.value)} placeholder="XXXXXX" maxLength={6} />
              <button className="btn-primary" onClick={verifyOTP} disabled={loading}>
                {loading ? 'Vérification...' : 'Vérifier'}
              </button>
              <button className="btn-link" onClick={() => setAuthStep('phone')}>← Changer le numéro</button>
            </>
          )}

          {authStep === 'select' && (
            <>
              <h3 className="select-title">Sélectionnez votre établissement</h3>
              <div className="facility-list">
                {facilities.map((f, i) => (
                  <button key={i} className="facility-option" onClick={() => selectFacility(f.id)}>
                    <strong>{f.name_fr}</strong>
                    <span>{f.quarter}, {f.city} — {f.type_fr || 'Établissement'}</span>
                    {f.is_emergency && <span className="emergency-tag">🚨 Urgences</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // DASHBOARD
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="logo-icon-sm">🏥</span>
          <div>
            <h2>MedLynk</h2>
            <p>{getFacilityType()} Workspace</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button className={page === 'dashboard' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('dashboard')}>
            📊 Dashboard
          </button>
          <button className={page === 'profile' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('profile')}>
            🏥 Profil
          </button>
          <button className={page === 'services' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('services')}>
            🩺 Services / Spécialisations
          </button>
          {(facility?.has_lab || getFacilityType() === 'Laboratoire') && (
            <button className={page === 'lab' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('lab')}>
              🔬 Laboratoire
            </button>
          )}
          <button className={page === 'hours' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('hours')}>
            🕐 Horaires & Contact
          </button>
          <button className={page === 'team' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('team')}>
            👥 Équipe
          </button>
          <button className={page === 'reports' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('reports')}>
            📋 Rapports
          </button>
          <button className={page === 'settings' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('settings')}>
            ⚙️ Paramètres
          </button>
        </nav>

        {facility && (
          <div className="sidebar-facility">
            <strong>{facility.name_fr}</strong>
            <span>{facility.quarter}, {facility.city}</span>
            {facility.listing_status === 'verified' && <span className="verified-badge">✓ Vérifié</span>}
          </div>
        )}

        <button className="btn-logout" onClick={logout}>Se déconnecter</button>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <h1>{page === 'dashboard' ? 'Dashboard' : page === 'profile' ? 'Profil de l\'établissement' : page === 'services' ? 'Services & Spécialisations' : page === 'lab' ? 'Services de Laboratoire' : page === 'hours' ? 'Horaires & Contact' : page === 'team' ? 'Gestion de l\'Équipe' : page === 'reports' ? 'Rapports' : 'Paramètres'}</h1>
          <p>{facility?.name_fr || 'Établissement'}</p>
        </header>

        {loading && <div className="loading">Chargement...</div>}

        {/* DASHBOARD */}
        {page === 'dashboard' && !loading && facility && (
          <div className="dashboard">
            <div className="stats-grid">
              <div className="stat-card">
                <p className="stat-label">Statut de vérification</p>
                <h2 className="stat-value verified-text">{facility.listing_status === 'verified' ? '✓ Vérifié' : facility.listing_status}</h2>
                <span className="stat-sub">{facility.listing_status === 'verified' ? 'Approuvé par MedLynk' : 'En attente'}</span>
              </div>
              <div className="stat-card green">
                <p className="stat-label">Spécialisations</p>
                <h2 className="stat-value">{specialisations.length}</h2>
                <span className="stat-sub">services actifs</span>
              </div>
              <div className="stat-card blue">
                <p className="stat-label">Services labo</p>
                <h2 className="stat-value">{labServices.length}</h2>
                <span className="stat-sub">tests disponibles</span>
              </div>
              <div className="stat-card amber">
                <p className="stat-label">Profil public</p>
                <h2 className="stat-value">{facility.is_active ? 'Publié' : 'Masqué'}</h2>
                <span className="stat-sub">Visible sur MedLynk</span>
              </div>
            </div>

            <div className="info-grid">
              <div className="info-card">
                <h3>Informations</h3>
                <div className="detail-row"><span>📍 Adresse</span><strong>{facility.address}</strong></div>
                <div className="detail-row"><span>🏘️ Quartier</span><strong>{facility.quarter}</strong></div>
                <div className="detail-row"><span>📞 Téléphone</span><strong>{facility.phone_number}</strong></div>
                <div className="detail-row"><span>📋 Type</span><strong>{getFacilityType()}</strong></div>
              </div>
              <div className="info-card">
                <h3>Services disponibles</h3>
                <div className="tags-container">
                  {facility.is_emergency && <span className="tag red">🚨 Urgences</span>}
                  {facility.has_lab && <span className="tag teal">🔬 Laboratoire</span>}
                  {facility.has_pharmacy && <span className="tag green">💊 Pharmacie</span>}
                  {facility.has_ambulance && <span className="tag red">🚑 Ambulance</span>}
                  {!facility.is_emergency && !facility.has_lab && !facility.has_pharmacy && <span className="tag gray">Aucun tag défini</span>}
                </div>
              </div>
              <div className="info-card">
                <h3>Spécialisations</h3>
                <div className="tags-container">
                  {specialisations.map((s, i) => (
                    <span key={i} className="tag purple">{s.name_fr}</span>
                  ))}
                  {specialisations.length === 0 && <span className="tag gray">Aucune spécialisation</span>}
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Actions rapides</h3>
              <div className="quick-actions">
                <button className="action-btn" onClick={() => setPage('profile')}>🏥 Modifier le profil</button>
                <button className="action-btn" onClick={() => setPage('services')}>🩺 Gérer les services</button>
                <button className="action-btn" onClick={() => setPage('hours')}>🕐 Modifier les horaires</button>
                {(facility?.has_lab || getFacilityType() === 'Laboratoire') && (
                  <button className="action-btn" onClick={() => setPage('lab')}>🔬 Gérer le laboratoire</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PROFILE */}
        {page === 'profile' && !loading && facility && (
          <div className="profile-page">
            <div className="profile-card">
              <h3>{facility.name_fr}</h3>
              {facility.name_en && <p className="text-muted">{facility.name_en}</p>}
              <div className="profile-details">
                <div className="detail-row"><span>📋 Type</span><strong>{getFacilityType()}</strong></div>
                <div className="detail-row"><span>📍 Adresse</span><strong>{facility.address}</strong></div>
                <div className="detail-row"><span>🏘️ Quartier</span><strong>{facility.quarter}</strong></div>
                <div className="detail-row"><span>🌆 Ville</span><strong>{facility.city}</strong></div>
                <div className="detail-row"><span>📞 Téléphone</span><strong>{facility.phone_number || '—'}</strong></div>
                <div className="detail-row"><span>💬 WhatsApp</span><strong>{facility.whatsapp_number || '—'}</strong></div>
                <div className="detail-row"><span>🚨 Urgences</span><strong>{facility.emergency_number || '—'}</strong></div>
                <div className="detail-row"><span>📋 Statut</span><strong className="verified-text">{facility.listing_status === 'verified' ? '✓ Vérifié par MedLynk' : facility.listing_status}</strong></div>
                <div className="detail-row"><span>📍 GPS</span><strong>{facility.latitude}, {facility.longitude}</strong></div>
                <div className="detail-row"><span>🚨 Service urgences</span><strong>{facility.is_emergency ? 'Oui' : 'Non'}</strong></div>
                <div className="detail-row"><span>🔬 Laboratoire</span><strong>{facility.has_lab ? 'Oui' : 'Non'}</strong></div>
                <div className="detail-row"><span>💊 Pharmacie</span><strong>{facility.has_pharmacy ? 'Oui' : 'Non'}</strong></div>
                <div className="detail-row"><span>🚑 Ambulance</span><strong>{facility.has_ambulance ? 'Oui' : 'Non'}</strong></div>
              </div>
            </div>
          </div>
        )}

        {/* SERVICES & SPECIALISATIONS */}
        {page === 'services' && !loading && (
          <div className="services-page">
            <div className="dashboard-section">
              <h3>Spécialisations actuelles ({specialisations.length})</h3>
              <div className="tags-container">
                {specialisations.map((s, i) => (
                  <span key={i} className="tag purple">{s.name_fr} / {s.name_en}</span>
                ))}
                {specialisations.length === 0 && <p className="text-muted">Aucune spécialisation ajoutée</p>}
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Ajouter une spécialisation</h3>
              <div className="spec-grid">
                {allSpecs.filter(s => !specialisations.find(sp => sp.name_fr === s.name_fr)).map((s, i) => (
                  <button key={i} className="spec-btn" onClick={() => addSpecialisation(s.id)}>
                    + {s.name_fr}
                  </button>
                ))}
                {allSpecs.filter(s => !specialisations.find(sp => sp.name_fr === s.name_fr)).length === 0 && (
                  <p className="text-muted">Toutes les spécialisations sont ajoutées</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LAB SERVICES */}
        {page === 'lab' && !loading && (
          <div className="lab-page">
            <div className="dashboard-section">
              <h3>Services de laboratoire ({labServices.length})</h3>
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Test / Service</th>
                    <th>Nom (EN)</th>
                    <th>Prix (FCFA)</th>
                    <th>Délai (heures)</th>
                    <th>Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {labServices.map((ls, i) => (
                    <tr key={i}>
                      <td><strong>{ls.name_fr}</strong></td>
                      <td>{ls.name_en || '—'}</td>
                      <td>{ls.price_fcfa ? `${ls.price_fcfa} FCFA` : '—'}</td>
                      <td>{ls.turnaround_hrs ? `${ls.turnaround_hrs}h` : '—'}</td>
                      <td><span className={ls.is_available ? 'tag green' : 'tag red'}>{ls.is_available ? 'Oui' : 'Non'}</span></td>
                    </tr>
                  ))}
                  {labServices.length === 0 && (
                    <tr><td colSpan={5} className="text-muted" style={{textAlign:'center',padding:'20px'}}>Aucun service de laboratoire</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="dashboard-section">
              <h3>Ajouter un service</h3>
              <div className="add-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Nom du test (FR)</label>
                    <input type="text" value={newLabName} onChange={e => setNewLabName(e.target.value)} placeholder="ex: Numération Formule Sanguine" />
                  </div>
                  <div className="form-group">
                    <label>Nom (EN)</label>
                    <input type="text" value={newLabNameEn} onChange={e => setNewLabNameEn(e.target.value)} placeholder="ex: Complete Blood Count" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prix (FCFA)</label>
                    <input type="number" value={newLabPrice} onChange={e => setNewLabPrice(e.target.value)} placeholder="ex: 5000" />
                  </div>
                  <div className="form-group">
                    <label>Délai (heures)</label>
                    <input type="number" value={newLabHours} onChange={e => setNewLabHours(e.target.value)} placeholder="ex: 24" />
                  </div>
                </div>
                <button className="btn-primary" onClick={addLabService}>+ Ajouter le service</button>
              </div>
            </div>
          </div>
        )}

        {/* HOURS & CONTACT */}
        {page === 'hours' && !loading && facility && (
          <div className="hours-page">
            <div className="dashboard-section">
              <h3>Horaires d'ouverture</h3>
              <div className="hours-grid">
                {['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'].map((day, i) => (
                  <div key={i} className="hours-row">
                    <span className="day-name">{day}</span>
                    <span className="hours-value">{i < 5 ? '08:00 — 18:00' : i === 5 ? '08:00 — 14:00' : 'Fermé'}</span>
                  </div>
                ))}
              </div>
              <p className="text-muted" style={{marginTop:'16px'}}>La modification des horaires sera disponible dans la prochaine mise à jour.</p>
            </div>
            <div className="dashboard-section">
              <h3>Informations de contact</h3>
              <div className="detail-row"><span>📞 Téléphone principal</span><strong>{facility.phone_number || '—'}</strong></div>
              <div className="detail-row"><span>🚨 Numéro urgences</span><strong>{facility.emergency_number || '—'}</strong></div>
              <div className="detail-row"><span>💬 WhatsApp</span><strong>{facility.whatsapp_number || '—'}</strong></div>
            </div>
          </div>
        )}

        {/* TEAM */}
        {page === 'team' && (
          <div className="empty-state">
            <span className="empty-icon">👥</span>
            <h3>Gestion de l'équipe</h3>
            <p>Invitez des membres de votre équipe à gérer le profil de l'établissement. Cette fonctionnalité sera disponible dans la prochaine mise à jour.</p>
          </div>
        )}

        {/* REPORTS */}
        {page === 'reports' && (
          <div className="empty-state">
            <span className="empty-icon">📊</span>
            <h3>Rapports bientôt disponibles</h3>
            <p>Les rapports d'activité et les statistiques de recherches patients seront disponibles dans la prochaine mise à jour.</p>
          </div>
        )}

        {/* SETTINGS */}
        {page === 'settings' && (
          <div className="settings-page">
            <div className="profile-card">
              <h3>Paramètres</h3>
              <div className="detail-row"><span>🌐 Langue</span><strong>Français</strong></div>
              <div className="detail-row"><span>🔔 Notifications</span><strong>Activées</strong></div>
              <div className="detail-row"><span>📱 Version</span><strong>1.0.0</strong></div>
              <div className="detail-row"><span>🏥 Établissement</span><strong>{facility?.name_fr}</strong></div>
              <button className="btn-link" onClick={() => { localStorage.removeItem('fac_id'); setSelectedFacilityId(null); setAuthStep('select'); loadFacilities(); }}>
                Changer d'établissement
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
